// The backend `/copy/` endpoint only duplicates the tournament's own fields
// (what the Information tab edits). Group/bracket stage structures, schedule
// timing overrides, and rating settings live in separate related resources
// and are not carried over automatically, so we replicate them here.
//
// NOTE: a newer backend build (not yet deployed here) has `/copy/` copy
// group-stage/draw-stage/schedule/rating itself - against THAT backend this
// file's copyGroupStages/copyDrawStages would double them up. Don't drop them
// again without first confirming which backend build is actually live.

const copyGroupStages = async (api, sourceId, targetId) => {
    try {
        const res = await api.get(`/api/v1.0/tournaments/${sourceId}/group-stage/`);
        const stages = res.data.results || [];

        for (const stage of stages) {
            const payload = {
                name: stage.name,
                group_quantity: stage.group_quantity,
                players_quantity: stage.players_quantity,
                games_in_set: stage.games_in_set,
                order: stage.order,
                // "Groups - place distribution" marker (null = plain Group
                // stage) - without this, a copied place-distribution stage
                // silently reverted to a plain qualifying group.
                start_place: stage.start_place,
            };
            // GroupStageListAPIView.perform_create does NOT call
            // sync_groups() (only perform_update does, see
            // TournamentDrawStagesSettings.js's own identical workaround) -
            // a bare POST left the stage configured (group_quantity/
            // players_quantity set) but with zero actual Group rows under
            // it, so the copied tournament's Групи tab showed nothing.
            // Follow up with a PATCH of the same payload to trigger
            // sync_groups() right away.
            const created = await api.post(`/api/v1.0/tournaments/${targetId}/group-stage/`, payload);
            await api.patch(`/api/v1.0/tournaments/${targetId}/group-stage/${created.data.id}/`, payload);
        }
    } catch (e) {
        console.error("Failed to copy group stages:", e);
    }
};

const copyDrawStages = async (api, sourceId, targetId) => {
    try {
        const res = await api.get(`/api/v1.0/tournaments/${sourceId}/draw-stage/`);
        const stages = res.data.results || [];

        for (const stage of stages) {
            await api.post(`/api/v1.0/tournaments/${targetId}/draw-stage/`, {
                name: stage.name,
                draw_type: stage.draw_type,
                players_quantity: stage.players_quantity,
                games_in_set: stage.games_in_set,
                draw_number: stage.draw_number,
            });
        }
    } catch (e) {
        console.error("Failed to copy draw stages:", e);
    }
};

const copyRatingSettings = async (api, sourceId, targetId) => {
    try {
        const tournamentRes = await api.get(`/api/v1.0/tournaments/${sourceId}/`);
        const rating = tournamentRes.data.rating;
        if (!rating) return;

        await api.patch(`/api/v1.0/tournaments/${targetId}/`, { rating });

        const settingsRes = await api.get(`/api/v1.0/tournaments/${sourceId}/rating-settings/`);
        const { rating_mode, rating_base_points, rating_tournament_coef, rating_decay_coef } = settingsRes.data;

        await api.patch(`/api/v1.0/tournaments/${targetId}/rating-settings/`, {
            rating_mode, rating_base_points, rating_tournament_coef, rating_decay_coef
        });
    } catch (e) {
        console.error("Failed to copy rating settings:", e);
    }
};

const copySchedule = async (api, sourceId, targetId) => {
    try {
        const [sourceRes, targetRes] = await Promise.all([
            api.get(`/api/v1.0/tournaments/${sourceId}/schedules/stages/`),
            api.get(`/api/v1.0/tournaments/${targetId}/schedules/stages/`),
        ]);
        const sourceStages = sourceRes.data.results || [];
        const targetStages = targetRes.data.results || [];

        if (!sourceStages.length || sourceStages.length !== targetStages.length) return;

        await Promise.all(targetStages.map((stage, i) =>
            api.patch(`/api/v1.0/tournaments/${targetId}/schedules/stages/${stage.id}/`, {
                start: sourceStages[i].start,
                court: sourceStages[i].court,
                round: sourceStages[i].round,
                interval: sourceStages[i].interval,
            })
        ));
    } catch (e) {
        console.error("Failed to copy schedule:", e);
    }
};

// onProgress(step) fires before each phase below, with a step key the
// caller maps to its own translated label - lets the UI show what's
// actually happening (e.g. in a progress toast) instead of a blank
// spinner for the whole several-request sequence.
export const copyTournamentFull = async (api, tournamentId, onProgress) => {
    onProgress?.("info");
    const copyRes = await api.post(`/api/v1.0/tournaments/${tournamentId}/copy/`, {});
    const newId = copyRes.data.id;

    onProgress?.("groups");
    await copyGroupStages(api, tournamentId, newId);
    onProgress?.("draws");
    await copyDrawStages(api, tournamentId, newId);
    onProgress?.("rating");
    await copyRatingSettings(api, tournamentId, newId);
    onProgress?.("schedule");
    await copySchedule(api, tournamentId, newId);

    return newId;
};
